import {
  inject,
  Injectable,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { UserProfileFacade } from '../../user-profile/facade/user-profile.facade';
import { CartFacade } from '../../cart/facade/cart.facade';
import { DeliveryFacade } from '../../delivery-mode/facade/delivery-facade.service';
import { CultureFacade } from '../../cultures/facade/cultures.facade';
import {
  AddressValidationService,
  CultureLanguageMap,
  FormService,
} from '@boels/helpers';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  Address,
  AddressValidationInput,
  AddressValidationResult,
  CheckoutInputLimitEnum,
  Country,
  GlobalMessageType,
  GoogleMapsAddressComponentType,
  PostalCode,
  UserEnum,
} from '@boels/models';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  filter,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
  throwError,
  withLatestFrom,
} from 'rxjs';
import { UserDataFacade } from '../../user-data/facade/user-data-facade';
import { GlobalMessageFacade } from '../../global-message/facade';

export interface DeliveryFormState {
  registerdUser: Signal<boolean>;
  deliveryForm: Signal<FormGroup>;
  isHouseNumberRequired: WritableSignal<boolean>;
  continueWithValidationIssue: WritableSignal<boolean>;
  postalCodePlaceholder: WritableSignal<string>;
  postalCodeValidationMessage: WritableSignal<string>;
  postalCodePositionOfSpace: WritableSignal<number>;
  countries: Signal<{ countries: Country[] }>;
  culture: Signal<string>;
  validationResult: WritableSignal<AddressValidationResult | undefined>;
}

@Injectable()
export class ShippingDetailsService {
  //Dependency injections
  private userProfileFacade = inject(UserProfileFacade);
  private cartFacade = inject(CartFacade);
  private deliveryFacade = inject(DeliveryFacade);
  private cultureFacade = inject(CultureFacade);
  private formService = inject(FormService);
  private addressValidationService = inject(AddressValidationService);
  private userDataFacade = inject(UserDataFacade);
  private messageService = inject(GlobalMessageFacade);

  get defaultState(): DeliveryFormState {
    const isRegisteredUser = () =>
      this.userDataFacade
        .userData()
        .pipe(map((user) => user.type === UserEnum.B2C_AUTHENTICATED));

    return {
      registerdUser: toSignal(isRegisteredUser(), { initialValue: false }),
      deliveryForm: signal(this.deliveryForm),
      isHouseNumberRequired: signal(false),
      continueWithValidationIssue: signal(false),
      postalCodePlaceholder: signal(''),
      postalCodeValidationMessage: signal(''),
      postalCodePositionOfSpace: signal(0),
      countries: toSignal(this.userProfileFacade.getCountries(), {
        initialValue: { countries: [] },
      }),
      culture: toSignal(this.cultureFacade.getActiveCulture(), {
        initialValue: '',
      }),
      validationResult: signal(undefined),
    };
  }

  prefillDeliveryForm(deliveryFormState: DeliveryFormState): Observable<null> {
    return this.cartFacade.getActiveCart().pipe(
      filter(Boolean),
      withLatestFrom(this.userDataFacade.userData()),
      take(1),
      switchMap(([cart, user]) => {
        if (cart.deliveryAddress) {
          let patchValue = cart.deliveryAddress;
          patchValue.operationalDirections = cart.operationalDirections;
          if (
            (cart.deliveryAddress?.defaultAddress ||
              (cart.deliveryAddress.shippingAddress &&
                cart.deliveryAddress.billingAddress)) &&
            user.type === UserEnum.B2C_AUTHENTICATED
          ) {
            const { titleCode, firstName, lastName, phone } = patchValue;
            patchValue = { titleCode, firstName, lastName, phone };
          }
          deliveryFormState.deliveryForm().patchValue({ ...patchValue });
        }

        return this.setHouseNumberValidation(deliveryFormState);
      }),
    );
  }

  submitDeliveryForm(
    deliveryFormState: DeliveryFormState,
  ): Observable<boolean> {
    const form = deliveryFormState.deliveryForm();

    return this.cultureFacade.getActiveLanguage().pipe(
      take(1),
      switchMap((lang: string) => {
        const userInputs = this.getGoogleAddressValidationInput(form, lang);
        return this.addressValidationService.validateAddress(lang, userInputs);
      }),
      tap((result: AddressValidationResult) => {
        deliveryFormState.validationResult.set(result);
      }),
      switchMap((result: AddressValidationResult) => {
        if (
          (!result.discrepancies && result.found) ||
          deliveryFormState.continueWithValidationIssue()
        ) {
          return this.verifyAndSetDeliveryAddress(deliveryFormState);
        } else {
          deliveryFormState.continueWithValidationIssue.set(true);
          return of(false);
        }
      }),
    );
  }

  displayError(error: string): void {
    this.messageService.add(
      { key: 'confirmation.error.text', scope: 'checkout' },
      GlobalMessageType.MSG_TYPE_ERROR,
    );
    throwError(() => error);
  }

  getFormItem<T extends FormControl | FormGroup>(
    form: FormGroup,
    key: string,
    nestedKey?: string,
  ): T {
    return this.formService.getFormItem(form, key, nestedKey) as T;
  }

  private get deliveryForm(): FormGroup {
    return new FormGroup({
      titleCode: new FormControl(''),
      firstName: new FormControl<string>(''),
      lastName: new FormControl<string>(''),
      line1: new FormControl<string>('', { validators: [Validators.required] }),
      line2: new FormControl<string>('', { validators: [Validators.required] }),
      postalCode: new FormControl<string>('', {
        validators: [Validators.required],
      }),
      town: new FormControl<string>('', { validators: [Validators.required] }),
      country: new FormGroup({
        isocode: new FormControl('', { validators: [Validators.required] }),
      }),
      phone: new FormControl<string>(''),
      operationalDirections: new FormControl<string>('', {
        validators: [
          Validators.maxLength(CheckoutInputLimitEnum.INSTRUCTION_S3),
        ],
      }),
      defaultAddress: new FormControl<boolean>(false),
      contactName: new FormControl<string>(''),
      contactPhone: new FormControl<string>(''),
    });
  }

  private setHouseNumberValidation(
    deliveryFormState: DeliveryFormState,
  ): Observable<null> {
    const form = deliveryFormState.deliveryForm();

    this.formService.setHouseNumberValidation(
      form,
      this.cultureFacade.getActiveCulture(),
    );

    const line2Control = form.get('line2');
    deliveryFormState.isHouseNumberRequired.set(
      line2Control ? this.formService.isRequired(line2Control) : false,
    );

    return this.setPostalCodeValidation(deliveryFormState);
  }

  private setPostalCodeValidation(
    deliveryFormState: DeliveryFormState,
  ): Observable<null> {
    const form = deliveryFormState.deliveryForm();

    return this.formService
      .setPostalCodeValidation(form, this.cultureFacade.getActiveCulture())
      .pipe(
        tap((postalCode: PostalCode | null) => {
          if (postalCode) {
            deliveryFormState.postalCodePlaceholder.set(
              postalCode.postalCodePlaceholder,
            );
            deliveryFormState.postalCodeValidationMessage.set(
              postalCode.postalCodeValidationMessage,
            );
            deliveryFormState.postalCodePositionOfSpace.set(
              postalCode.postalCodePositionOfSpace,
            );
          } else deliveryFormState.postalCodePlaceholder.set('');
        }),
        map(() => null),
      );
  }

  private getGoogleAddressValidationInput(
    form: FormGroup,
    lang: string,
  ): AddressValidationInput {
    const getValue = (key: string): string =>
      ((form.get(key)?.value as string) || '').trim();

    const values: Record<string, string> = {
      line1: getValue('line1'),
    };
    const fieldMap: Record<string, GoogleMapsAddressComponentType[]> = {
      line1: [GoogleMapsAddressComponentType.ROAD],
    };

    if (!lang.includes(CultureLanguageMap['en-gb'])) {
      values['line2'] = getValue('line2');
      fieldMap['line2'] = [GoogleMapsAddressComponentType.HOUSE_NUMBER];
    }

    values['town'] = getValue('town');
    values['postalCode'] = getValue('postalCode');

    fieldMap['town'] = [
      GoogleMapsAddressComponentType.POSTAL_TOWN,
      GoogleMapsAddressComponentType.TOWN_OR_CITY,
      GoogleMapsAddressComponentType.SUB_LOC,
    ];
    fieldMap['postalCode'] = [GoogleMapsAddressComponentType.POSTAL_CODE];

    return { values, fieldMap };
  }

  private verifyAndSetDeliveryAddress(
    deliveryFormState: DeliveryFormState,
  ): Observable<boolean> {
    const postalCodeControl = deliveryFormState
      .deliveryForm()
      .get('postalCode');
    const postalCode = this.formService.formatPostalCode(
      postalCodeControl?.value ?? '',
      deliveryFormState.postalCodePositionOfSpace(),
    );

    const shippingAddressValue = {
      ...deliveryFormState.deliveryForm().value,
      postalCode,
    } as Address;

    const phoneControl = this.getFormItem<FormControl>(
      deliveryFormState.deliveryForm(),
      'contactPhone',
    );
    if (phoneControl.value && typeof phoneControl.value === 'object') {
      shippingAddressValue.contactPhone = phoneControl.value
        .e164Number as string;
    }

    return this.deliveryFacade.verifyAddress(shippingAddressValue).pipe(
      withLatestFrom(this.cartFacade.getActiveCart()),
      switchMap(([backendValidationResult, cart]) => {
        const addressValue = shippingAddressValue as Address;
        addressValue.defaultAddress = false;
        return backendValidationResult.decision === 'ACCEPT'
          ? this.deliveryFacade
              .setShippingAddress(addressValue, cart?.operationalDirections)
              .pipe(
                tap((address: Address) =>
                  this.cartFacade.setDeliveryAddress(address),
                ),
                map(() => true),
              )
          : throwError(
              () =>
                new Error(backendValidationResult.errors?.errors.toString()),
            );
      }),
    );
  }
}
